import React, { forwardRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import "./websiteBuilderColorPickerPopup.scss";
import { useColorPickerController } from "../useColorPickerController";
import { HueSpectrum } from "./canvas/HueSpectrum";
import { SaturationBrightnessPanel } from "./canvas/SaturationBrightnessPanel";
import { WEBSITE_BUILDER_PORTAL } from "../../../WebsiteBuilderEditor";
import Portal from "elements/Portal";

export interface IWebsiteBuilderColorPickerPopupPosition {
  x: number;
  y: number;
}

interface IWebsiteBuilderColorPickerPopupProps {
  position: IWebsiteBuilderColorPickerPopupPosition;
  hexColor: string;
  updateHexColor: (updatedHexColor: string) => void;
  closePopup: () => void;
}

const WEBSITE_BUILDER_COLOR_PICKER_POPUP_HEIGHT = 335;
export const WEBSITE_BUILDER_COLOR_PICKER_POPUP_MARGIN = 10;
export const WEBSITE_BUILDER_COLOR_PICKER_POPUP_TOTAL_HEIGHT =
  WEBSITE_BUILDER_COLOR_PICKER_POPUP_HEIGHT + WEBSITE_BUILDER_COLOR_PICKER_POPUP_MARGIN;

export const WebsiteBuilderColorPickerPopup = forwardRef<HTMLDivElement, IWebsiteBuilderColorPickerPopupProps>(
  (props, colorPickerPopupRef) => {
    const { position, hexColor, updateHexColor, closePopup } = props;
    const { t } = useTranslation();

    const colorPickerController = useColorPickerController(hexColor, updateHexColor);

    const handleKeydown = useCallback((e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();

        closePopup();
      }
    }, []);

    useEffect(() => {
      document.addEventListener("keydown", handleKeydown, { capture: true });

      return () => document.removeEventListener("keydown", handleKeydown, { capture: true });
    }, [handleKeydown]);

    return (
      <Portal isMounted={true} parentId={WEBSITE_BUILDER_PORTAL}>
        <div
          ref={colorPickerPopupRef}
          className="website-builder-color-picker-popup"
          style={{
            left: position.x,
            top: position.y,
            height: WEBSITE_BUILDER_COLOR_PICKER_POPUP_HEIGHT,
          }}
          onPointerDown={e => e.stopPropagation()}
        >
          <SaturationBrightnessPanel
            rgb={colorPickerController.rgb}
            hsv={colorPickerController.hsv}
            dispatchUpdateHsv={colorPickerController.dispatchUpdateHsv}
          />
          <HueSpectrum hsv={colorPickerController.hsv} dispatchUpdateHsv={colorPickerController.dispatchUpdateHsv} />
          <div className="color-picker-popup-inputs">
            <Input
              className="color-picker-popup-hex-color-input"
              label={t("Hex")}
              value={colorPickerController.hexColorInput}
              onChange={e => colorPickerController.dispatchUpdateHexColorInput(e.target.value)}
            />
            <Input
              label="R"
              value={colorPickerController.rgbInput.r}
              onChange={e => colorPickerController.dispatchUpdateRgbInput("r", e.target.value)}
            />
            <Input
              label="G"
              value={colorPickerController.rgbInput.g}
              onChange={e => colorPickerController.dispatchUpdateRgbInput("g", e.target.value)}
            />
            <Input
              label="B"
              value={colorPickerController.rgbInput.b}
              onChange={e => colorPickerController.dispatchUpdateRgbInput("b", e.target.value)}
            />
          </div>
        </div>
      </Portal>
    );
  },
);

WebsiteBuilderColorPickerPopup.displayName = "WebsiteBuilderColorPickerPopup";
